import type { DiscoverPlayerDataResponse, DiscoverPlayerResponse } from "libs/apiClient"
import { ContentCell } from "./ContentCell";
import client from "libs/client";
import { UserPermissionsTable } from "features/discover/UserPermissionsTable";
import { useId, useState } from "react";
import { GenderOptions, PositionChoices } from "features/discover/types";
import { Link } from "react-router";
import { DiscoverGamesTable } from "./DiscoverGamesTable";
import { Tabs } from "./Tabs";
import { PlayerToPlayerDataMigrationDialog } from "features/discover/PlayerToPlayerDataMigrationDialog";

const tabs = [
	{
		label: "Discover permissions",
		key: "permissions"
	}, {
		label: "Games",
		key: "games",
	},
];

export function DiscoverPlayerDetail({
	player,
	playerData,
}: {
	player: DiscoverPlayerResponse
	playerData: DiscoverPlayerDataResponse
}) {
	const [tab, setTab] = useState<string | null>("discoverPermissions");
	const [playerToMigrate, setPlayerToMigrate] = useState<DiscoverPlayerResponse | null>(null);

	const contentProps = {
		id: player.id,
		mutation: client.patchDiscoverPlayer.useMutation(),
		invalidationKey: ['discoverPlayerDetail', 'discoverPlayerDataDetail'],
	};

	const playerProps = [
		{
			key: useId(),
			title: "First Name",
			field: "firstName",
			type: "string",
			value: playerData.firstName,
		},
		{
			key: useId(),
			title: "Last Name",
			field: "lastName",
			type: "string",
			value: playerData.lastName,
		},
		{
			key: useId(),
			title: "Team",
			type: "link",
			formatter: () => player.team ? <Link to={`/discover/team/${player.team.id}`} className="button-link">{player.team.name}</Link> : "-",
		},
		{
			key: useId(),
			title: "Clubhouse",
			type: "link",
			formatter: () => player.club ? <Link to={`/discover/club/${player.club.id}`} className="button-link">{player.club.name}</Link> : "-",
		},
		{
			key: useId(),
			title: "Gender",
			field: "gender",
			type: "list",
			value: playerData.gender,
			options: GenderOptions,
		},
		{
			key: useId(),
			title: "Position",
			field: "position",
			type: "list",
			value: player.position,
			options: PositionChoices,
		},
		{
			key: useId(),
			title: "Jersey number",
			field: "numberSign",
			type: "number",
			value: player.numberSign,
		},
		{
			key: useId(),
			title: "Player data owner",
			field: "isPlayerDataOwner",
			type: "boolean",
			value: player.isPlayerDataOwner,
		},
		{
			key: useId(),
			title: "Vip",
			field: "isVip",
			type: "boolean",
			value: player.isVip,
		},
	];

	const handleMigrationCancel = () => {
		setPlayerToMigrate(null);
	}

	const handleMigrationSuccess = () => {
		setPlayerToMigrate(null);
	}

    return (
		<>
			<div className="discover-card__pre-header">
				<div className="discover-card__title">
					{player.fullName}, {player.playerType} player, {player.id}
				</div>
				{!!playerData && <span
					className="admin-button admin-button--primary"
					onClick={() => setPlayerToMigrate(player)}
				>Migrate membership</span>}
			</div>
			<div className="discover-card">
				<div className="cell-grid">
					{playerProps.map((props) => {
						return <ContentCell {...contentProps} {...props} key={props.key} />
					})}
				</div>
			</div>

			<Tabs items={tabs} active={tab} onClick={(key) => setTab(key)} />

			{tab === "games" && <div className="discover-card" style={{ overflowX: 'auto' }}>
				<DiscoverGamesTable defaultFilters={{
					playerId: player.id,
				}} />
			</div>}

			{tab === "discoverPermissions" && <div className="discover-card">
				<UserPermissionsTable userPermissions={player?.userPermissions || []} />
			</div>}

			{playerToMigrate !== null && !!playerData && <PlayerToPlayerDataMigrationDialog
				onSuccess={handleMigrationSuccess}
				onCancel={handleMigrationCancel}
				playerData={playerData}
				playerToMigrate={playerToMigrate}
			/>}
		</>
	)
}
